import { Injectable, inject } from '@angular/core';
import {
  CancelReservedOrderItemsAndSubscriptionsRequestDto,
  FinancialTransactionRequestDto,
  GetOrderItemDetailsResponseDto,
  OrderArticleProductResponseDto,
  OrderArticleSeatResponseDto,
  OrderItemDetailsResponseDto,
  OrderItemPaymentItemResponseDto,
  OrderItemPaymentResponseDto,
  OrderItemRowSubscriptionResponseDto,
  OrderItemStatusEnumResponseDto,
  CancelOrderItemsRequest,
  OrderSeatsCancelRequestDto,
  OrderStatusEnumResponseDto,
  PaymentModeResponseDto,
  PaymentOperationTypeEnumRequestDto,
  PaymentOperationTypeEnumResponseDto,
} from '@core/services/apiclient';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store/index';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { MoneyService } from '@shared/services/money.service';
import { catchError, forkJoin, map, Observable, of, take } from 'rxjs';
import {
  isSubscriptionGroup,
  OrderDisplayType,
  OrdersActionLevel,
  TreeListRowData,
} from '../models/orders.model';
import { OrdersConfirmDialogService } from './orders-confirm-dialog.service';
import { OrdersService } from './orders.service';
import { PaymentTransactionService } from './payment-transaction.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersCancelService {
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly store = inject<Store<fromOrders.State>>(Store);
  private readonly translate = inject(TranslateService);
  private readonly moneyService = inject(MoneyService);
  private readonly ordersService = inject(OrdersService);
  private readonly ordersConfirmDialogService = inject(OrdersConfirmDialogService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);

  public getCancellableFolders(folders: GetOrderItemDetailsResponseDto[]) {
    return (folders || []).filter(
      ({ status }) =>
        status !== OrderStatusEnumResponseDto.Reserved &&
        status !== OrderStatusEnumResponseDto.Canceled &&
        status !== OrderStatusEnumResponseDto.Released,
    );
  }

  public CalculateCurrentAmount = (
    orderPayment: OrderItemPaymentItemResponseDto,
    paybackAmount: number,
  ): [number, number] => {
    let amount = orderPayment?.amount;
    if (paybackAmount > 0) {
      if (orderPayment?.amount > paybackAmount) {
        amount = orderPayment?.amount - paybackAmount;
        paybackAmount = 0;
      } else {
        amount = 0;
        paybackAmount -= orderPayment?.amount;
      }
    }
    return [amount, paybackAmount];
  };

  public mapOrderPaymentToPaymentMode = (
    orderPayment: OrderItemPaymentItemResponseDto,
    paymentModeitems: PaymentModeResponseDto[],
    amount: number,
  ) => {
    return <SalesPaymentModeModel>{
      amount: amount,
      paymentTypeLabel: orderPayment?.mode.name,
      paymentTypeId: orderPayment.mode.id,
      paymentMode: this.getPaymentMode(orderPayment?.mode?.id, paymentModeitems),
    };
  };

  public getPaymentMode(paymentModeId: number, paymentModeitems: PaymentModeResponseDto[]) {
    return (paymentModeitems || []).find(({ id }) => id === paymentModeId);
  }

  public mapOrderPaymentToRefundsPayload = (
    orderPayment: OrderItemPaymentItemResponseDto,
    amount: number,
  ) => {
    return <FinancialTransactionRequestDto>{
      id: orderPayment?.id,
      paymentId: orderPayment?.paymentId,
      paymentModeId: orderPayment?.paymentModeId,
      label: orderPayment?.mode.name,
      amount: amount,
      paymentOperationType: PaymentOperationTypeEnumRequestDto.Refund,
    };
  };

  public getPaybackAmount = (allOrderPayments: OrderItemPaymentResponseDto[]) => {
    const paybackItems = (allOrderPayments || []).filter(
      ({ operationType }) => operationType === PaymentOperationTypeEnumResponseDto.Refund,
    );
    return Math.abs(
      (paybackItems || []).reduce((acc, curr) => {
        return acc + curr?.items?.[0].amount;
      }, 0),
    );
  };

  public setRefundsPayload = (paymentMode: PaymentModeResponseDto, refundAmount: number) => {
    return <FinancialTransactionRequestDto>{
      id: -1,
      paymentId: -1,
      paymentModeId: paymentMode?.id,
      label: paymentMode?.name,
      amount: refundAmount,
      paymentOperationType: PaymentOperationTypeEnumRequestDto.Refund,
    };
  };

  public getOrderItemsPayments(orderItemId: number): Observable<OrderItemPaymentResponseDto[]> {
    return this.ordersService.getOrderItemsPayments(orderItemId);
  }

  public showFooterSummaryItem = (item: string, displayType: OrderDisplayType) => {
    switch (item) {
      case 'folder': {
        switch (displayType) {
          case OrderDisplayType.SingleSeatSelection:
          case OrderDisplayType.MultipleSeatSelection:
          case OrderDisplayType.SingleFolderSelection:
            return false;
          case OrderDisplayType.MultipleFolderSelection:
          case OrderDisplayType.OrderSelection:
            return true;
        }
      }
      case 'seat': {
        switch (displayType) {
          case OrderDisplayType.SingleSeatSelection:
            return false;
          default:
            return true;
        }
      }
    }
  };

  public getTotalAmount = (
    displayType: OrderDisplayType,
    cancelFolders: OrderItemDetailsResponseDto[],
    cancelseats: OrderArticleSeatResponseDto[],
  ) => {
    if (!cancelFolders?.length && !cancelseats?.length) {
      return of(0);
    }

    switch (displayType) {
      case OrderDisplayType.SingleSeatSelection:
        return of(this.moneyService.fromCents(this.moneyService.toCents(cancelseats[0]?.price?.amount)));
      case OrderDisplayType.MultipleSeatSelection:
        return of(this.moneyService.calculateTotalAmount(cancelseats, (seat) => seat?.price?.amount || 0));
      case OrderDisplayType.SingleFolderSelection:
        return of(this.moneyService.fromCents(this.moneyService.toCents(cancelFolders[0]?.amount)));
      case OrderDisplayType.MultipleFolderSelection:
        return of(this.moneyService.calculateTotalAmount(cancelFolders, (folder) => folder?.amount || 0));
      case OrderDisplayType.OrderSelection:
        if (cancelFolders?.length === 1) {
          return of(this.moneyService.fromCents(this.moneyService.toCents(cancelFolders[0]?.amount)));
        }
        return of(this.moneyService.calculateTotalAmount(cancelFolders, (folder) => folder?.amount || 0));
    }
  };

  public processSeatsReservedCancel(
    level: OrdersActionLevel,
    displayType: OrderDisplayType,
    cancelseats: OrderArticleSeatResponseDto[] | OrderArticleProductResponseDto[],
    orderId: number,
  ) {
    const isProduct = !(cancelseats?.[0] as OrderArticleSeatResponseDto)?.seatInfos;
    let title = this.translate.instant(
      isProduct ? 'orders.info.areYouSureWantCancelProduct' : 'orders.info.areYouSureWantCancelPlace',
    );
    let subtitle = this.translate.instant(
      isProduct ? 'orders.info.selectedProductWillBeReleased' : 'orders.info.selectedPlaceWillBeReleased',
    );
    let validateBtnLabel = this.translate.instant(
      isProduct ? 'orders.info.yesConfirmAnnulationProduct' : 'orders.info.yesConfirmAnnulationSeat',
    );
    if (displayType === OrderDisplayType.MultipleSeatSelection) {
      title = this.translate.instant('orders.info.areYouSureWantCancelSelection');
      subtitle = this.translate.instant('orders.info.placesOfSelectionWillBeReleased');
      validateBtnLabel = this.translate.instant('orders.info.yesConfirmAnnulationSelection');
    }
    this.confirmDialogService.confirm({
      title: title,
      subtitle: subtitle,
      validateBtnLabel: validateBtnLabel,
      cancelBtnLabel: this.translate.instant('orders.info.backToSelection'),
      iconName: 'type=danger',
      validate: () => {
        const orderSeatsCancel: OrderSeatsCancelRequestDto = {
          refunds: [],
          seatIds:
            displayType === OrderDisplayType.SingleSeatSelection
              ? [cancelseats[0]?.id]
              : cancelseats?.map((seat) => seat?.id),
        };

        this.store.dispatch(
          fromOrders.actions.processSeatsCancel({
            orderArticles: {
              orderID: orderId,
              orderItemId: cancelseats?.[0].orderItemId,
              orderArticleItems: cancelseats,
            },
            orderSeatsCancel: orderSeatsCancel,
            level: level,
          }),
        );
      },
      cancel: () => {
        this.store.dispatch(fromOrders.actions.resetOrderAction());
      },
    });
  }

  public processOrderReservedCancel(
    level: OrdersActionLevel,
    displayType: OrderDisplayType,
    cancelFolders: OrderItemDetailsResponseDto[],
  ) {
    let title = this.translate.instant('orders.info.areYouSureWantCancelOrder');
    let subtitle = this.translate.instant('orders.info.placesOfOrderWillBeReleased');
    let validateBtnLabel = this.translate.instant('orders.info.yesConfirmAnnulationOrder');
    if (level === OrdersActionLevel.List && displayType === OrderDisplayType.MultipleFolderSelection) {
      title = this.translate.instant('orders.info.areYouSureWantCancelSelection');
      subtitle = this.translate.instant('orders.info.placesOfSelectionWillBeReleased');
      validateBtnLabel = this.translate.instant('orders.info.yesConfirmAnnulationSelection');
    } else if (
      level === OrdersActionLevel.FolderDetails ||
      (level === OrdersActionLevel.List && displayType === OrderDisplayType.SingleFolderSelection)
    ) {
      title = this.translate.instant('orders.info.areYouSureWantCancelFolder');
      subtitle = this.translate.instant('orders.info.placesOfFolderWillBeReleased');
      validateBtnLabel = this.translate.instant('orders.info.yesConfirmAnnulationFolder');
    }

    this.confirmDialogService.confirm({
      title: title,
      subtitle: subtitle,
      validateBtnLabel: validateBtnLabel,
      cancelBtnLabel: this.translate.instant('orders.info.backToSelection'),
      iconName: 'type=danger',
      validate: () => {
        const orderItemsToCancel: CancelOrderItemsRequest = {
          refunds: [],
          orderItemIds:
            displayType === OrderDisplayType.SingleFolderSelection
              ? [cancelFolders[0]?.id]
              : cancelFolders?.map((folder) => folder?.id),
        };

        this.store.dispatch(
          fromOrders.actions.processOrderCancel({
            orderItemCancel: orderItemsToCancel,
            orderId:
              displayType === OrderDisplayType.SingleFolderSelection
                ? cancelFolders[0]?.orderId
                : cancelFolders?.[0].orderId,
            level: level,
          }),
        );
      },
      cancel: () => {
        this.store.dispatch(fromOrders.actions.resetOrderAction());
      },
    });
  }

  public cancelReservedSelection(
    cancelFolders: TreeListRowData[],
    executor: (request: CancelReservedOrderItemsAndSubscriptionsRequestDto) => void,
  ): void {
    const proceed = () => {
      const request = this.paymentTransactionService.buildCancelReservedRequest(cancelFolders);
      executor(request);
    };
    const showConfirm = () =>
      this.confirmDialogService.confirm({
        title: this.translate.instant('orders.info.areYouSureWantCancelSelection'),
        subtitle: this.translate.instant('orders.info.placesOfSelectionWillBeReleased'),
        validateBtnLabel: this.translate.instant('orders.info.yesConfirmAnnulationSelection'),
        cancelBtnLabel: this.translate.instant('orders.info.backToSelection'),
        iconName: 'type=danger',
        validate: proceed,
        cancel: () => {},
      });

    // Annulation PARTIELLE d'une formule valorisée à 0,00 € (abonnement) : avertissement non bloquant
    // avant l'annulation, en cohérence avec le volet (GEN-6319, ticket « … partiel », « volet ET liste »).
    // « Partiel » se déduit des compteurs du groupe de souscription ; le « 0 € » n'ayant pas de signal
    // fiable sur la ligne, on résout les montants réservés via getSubscriptionOrderItems (comme le volet).
    const partialFormulaGroups = (cancelFolders ?? []).filter(
      (row) =>
        isSubscriptionGroup(row) &&
        (row.reservedOrderRowsCount ?? 0) <
          (row.orderItemCount ?? 0) - (row.canceledOrderRowsCount ?? 0),
    );
    const subscriptionIds = partialFormulaGroups
      .map((group) => group.subscriptionOrderId ?? group.id)
      .filter((id) => id != null && id > 0);
    if (!subscriptionIds.length) {
      showConfirm();
      return;
    }

    forkJoin(subscriptionIds.map((id) => this.ordersService.getSubscriptionOrderItems(id)))
      .pipe(
        map((results) => results.flat()),
        catchError(() => of([] as OrderItemRowSubscriptionResponseDto[])),
        take(1),
      )
      .subscribe((items) => {
        const reservedSeances = (items ?? []).filter(
          (item) => item.statusGroup === OrderItemStatusEnumResponseDto.Reserved,
        );
        const total = this.moneyService.calculateTotalAmount(
          reservedSeances,
          (item) => item?.amount || 0,
        );
        if (reservedSeances.length && total === 0) {
          this.ordersConfirmDialogService.showZeroAmountFormulaWarning('cancel', proceed, () => {});
        } else {
          showConfirm();
        }
      });
  }

  public isSeats = (displayType: OrderDisplayType) => {
    switch (displayType) {
      case OrderDisplayType.SingleSeatSelection:
      case OrderDisplayType.MultipleSeatSelection:
        return true;
      case OrderDisplayType.MultipleFolderSelection:
      case OrderDisplayType.OrderSelection:
      case OrderDisplayType.SingleFolderSelection:
      // Le périmètre « places d'une formule » n'est pas une sélection de places de la séance
      // affichée : son annulation ne passe pas par le dialog de place.
      case OrderDisplayType.SubscriptionSeatsSelection:
        return false;
    }
  };

  private refreshOrdersListFilters(): void {
    this.store
      .select(fromOrders.selectors.selectOrderItemsFilters)
      .pipe(take(1))
      .subscribe({
        next: (filters) => {
          this.store.dispatch(fromOrders.actions.setOrdersListFilters({ filters: [...(filters ?? [])] }));
        },
      });
  }
}
